/**
 * Flags, drawn from flag-icons (271 ISO 3166-1 SVGs, imported once in
 * index.css) rather than from emoji.
 *
 * Emoji regional indicators were the first cut and cost nothing, but they are
 * flags only on macOS, iOS and Android — Windows has no such glyphs and renders
 * the bare letters. An SVG is a flag everywhere, which is the point of showing
 * one.
 *
 * The region still comes from the data: a locale for a language, an ISO code for
 * a country. Nothing here guesses.
 */

/**
 * regionOf pulls the region subtag out of a locale.
 *
 * The separator is deliberately either dash or underscore: production mixes
 * `en-US` with `el_GR`, and the catalogue stores both verbatim rather than
 * normalising a code every consumer resolves by string.
 *
 * A code with no separator has no region — `BE` is BetRadar's, not Belgium's,
 * and guessing there would put a Belgian flag on a row that is not a language.
 */
export function regionOf(locale: string): string {
  const parts = locale.trim().split(/[-_]/)
  if (parts.length < 2) return ''
  const region = parts[parts.length - 1]
  return /^[A-Za-z]{2}$/.test(region) ? region.toUpperCase() : ''
}

/** classOf is the flag-icons class for a region, or '' when there is none. */
export function classOf(region: string): string {
  return /^[A-Z]{2}$/.test(region) ? `fi fi-${region.toLowerCase()}` : ''
}

/**
 * Flag renders a flag, from a locale (`it-IT`) or from an explicit region code
 * (`IT`) when the caller already has one — a country row, say.
 *
 * The two are separate props on purpose. Treating any two-letter string as a
 * region would put a Belgian flag on BetRadar, whose local code is `BE` and
 * which is not a language, let alone a country.
 *
 * It falls back to a muted dash so the column keeps its rhythm.
 */
export function Flag({
  locale,
  region: explicitRegion,
  className = '',
}: {
  locale?: string
  region?: string
  className?: string
}) {
  const region = explicitRegion ? explicitRegion.trim().toUpperCase() : regionOf(locale ?? '')
  const flagClass = classOf(region)

  if (!flagClass) {
    return <span className={`text-muted-foreground ${className}`}>—</span>
  }
  return (
    <span
      // The ring keeps a mostly-white flag (Japan, Poland) from dissolving into
      // the row; flag-icons itself draws no border. A code the set does not
      // cover leaves an empty box, and the muted fill makes that read as
      // "no flag" rather than as a broken image.
      className={`${flagClass} rounded-[2px] bg-muted align-middle ring-1 ring-border/60 ${className}`}
      title={region}
      role="img"
      aria-label={region}
    />
  )
}
